import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import PaginatedListState from 'flarum/common/states/PaginatedListState';
import Discussion from 'flarum/common/models/Discussion';
import avatar from 'flarum/common/helpers/avatar';
import icon from 'flarum/common/helpers/icon';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import { truncate } from 'flarum/common/utils/string';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import ItemList from 'flarum/common/utils/ItemList';
import Link from 'flarum/common/components/Link';
import LogInModal from 'flarum/forum/components/LogInModal';

app.initializers.add('custom/discussions-item', () => {
  
  // 扩展DiscussionListState的requestParams，确保API请求包含firstPost和likes关系
  extend(DiscussionListState.prototype, 'requestParams', function (params) {
    if (typeof params.include === 'string') {
      params.include = [params.include];
    }
    if (!params.include) {
      params.include = [];
    }
    if (params.include.indexOf('firstPost') === -1) {
      params.include.push('firstPost');
    }
    // 明确添加 firstPost.likes 关系
    if (params.include.indexOf('firstPost.likes') === -1) {
      params.include.push('firstPost.likes');
    }
    
    // 如果sort参数为空或未定义，默认设置为newest（按创建时间最新）
    if (!params.sort || params.sort === '') {
      params.sort = '-createdAt'; // newest 对应的排序值
    }
  });
  
  // 覆盖sortMap：默认newest，仅保留newest和top（移除latest、oldest）
  override(DiscussionListState.prototype, 'sortMap', function (original) {
    const map = original();
    const newMap = {};
    if (map.relevance !== undefined) {
      newMap.relevance = map.relevance;
    }
    newMap.newest = map.newest;
    newMap.top = map.top;
    return newMap;
  });

  // 覆盖DiscussionListState的loadPage，强制始终通过API请求获取数据，不使用预加载数据
  override(DiscussionListState.prototype, 'loadPage', function (original, page) {
    // 直接调用父类PaginatedListState的loadPage方法，跳过预加载数据检查
    return PaginatedListState.prototype.loadPage.call(this, page);
  });
  
  // 用于跟踪当前排序状态，确保 UI 能正确更新
  let currentSortState = null;
  
  // 扩展IndexPage的viewItems，修改sort切换行为，避免整页刷新
  extend(IndexPage.prototype, 'viewItems', function (items) {
    // 替换sort下拉菜单，使用无刷新切换
    if (items.has('sort')) {
      const sortMap = app.discussions.sortMap();
      const sortOptions = Object.keys(sortMap).reduce((acc, sortId) => {
        // newest 显示为 Latest
        acc[sortId] = sortId === 'newest'
          ? app.translator.trans('core.forum.index_sort.latest_button')
          : app.translator.trans(`core.forum.index_sort.${sortId}_button`);
        return acc;
      }, {});
      
      // 获取当前排序值，优先使用状态变量，否则从 URL 读取（已移除的 latest/oldest 视为默认）
      const getCurrentSort = () => {
        if (currentSortState !== null) {
          return currentSortState;
        }
        const defaultSort = Object.keys(sortMap)[0];
        const urlSort = m.route.param('sort');
        if (urlSort !== undefined && urlSort !== null && urlSort !== '' && sortMap[urlSort] !== undefined) {
          currentSortState = urlSort;
          return urlSort;
        }
        currentSortState = defaultSort;
        return defaultSort;
      };
      
      const currentSort = getCurrentSort();
      
      items.replace('sort',
        <Dropdown
          buttonClassName="Button"
          label={sortOptions[currentSort] || Object.keys(sortMap).map((key) => sortOptions[key])[0]}
          accessibleToggleLabel={app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label')}
          key={`sort-dropdown-${currentSort}`}
        >
          {Object.keys(sortOptions).map((value) => {
            const label = sortOptions[value];
            const active = getCurrentSort() === value;
            
            return (
              <Button 
                icon={active ? 'fa-light fa-check' : true} 
                onclick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  currentSortState = value;
                  const params = app.search.params();
                  
                  if (value === Object.keys(sortMap)[0]) {
                    delete params.sort;
                  } else {
                    params.sort = value;
                  }
                  
                  // 更新 URL（不触发整页刷新）
                  const url = app.route(app.current.get('routeName'), params);
                  window.history.replaceState(null, '', url);
                  
                  // 先清空列表再刷新，确保 refreshParams 会触发数据重新加载
                  app.discussions.clear();
                  app.discussions.refreshParams(params, 1).then(() => m.redraw());
                }} 
                active={active}
              >
                {label}
              </Button>
            );
          })}
        </Dropdown>
      );
    }
  });
  
  // 扩展IndexPage的actionItems，修改刷新按钮和标记已读按钮文案
  extend(IndexPage.prototype, 'actionItems', function (items) {
    // 修改刷新按钮文案为"Refresh"
    if (items.has('refresh')) {
      items.replace('refresh', 
        <Button
          title={app.translator.trans('core.forum.index.refresh_tooltip')}
          icon="fa-light fa-sync"
          className="Button"
          onclick={() => {
            app.discussions.refresh();
            if (app.session.user) {
              app.store.find('users', app.session.user.id());
              m.redraw();
            }
          }}
        >
          Refresh
        </Button>
      );
    }
    
    // 修改标记已读按钮文案为"Mark as read"
    if (items.has('markAllAsRead')) {
      items.replace('markAllAsRead', 
        <Button
          title={app.translator.trans('core.forum.index.mark_all_as_read_tooltip')}
          icon="fa-light fa-check"
          className="Button"
          onclick={this.markAllAsRead.bind(this)}
        >
          Mark as read
        </Button>
      );
    }
  });
  
  // 扩展DiscussionListItem组件，添加缩略图、用户头像、Likes和回帖用户头像列表
  // 注意：enhancedInfo 现在通过 infoItems 添加，不再从这里添加


  // 判断是否为 emoji 图片 URL（不作为缩略图）
  const isEmojiImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return /\/images\/emoji\/|\/emoji\/twitter\/|\/emoji\/facebook\//i.test(url);
  };

  // 获取缩略图URL：课程类型优先用 coverImage，否则用 customThumbnail
  DiscussionListItem.prototype.getThumbnailUrl = function () {
    const discussion = this.attrs.discussion;
    const attrs = discussion.data?.attributes || {};
    const customThumbnail = attrs.customThumbnail;
    const enableThumbnail = app.forum.attribute('custom-discussions-item.enable_thumbnail') !== false;
    const courseInfo = discussion.attribute ? discussion.attribute('groupCourse') : attrs.groupCourse;
    if (!enableThumbnail) return null;
    // 课程类型：优先使用后台配置的 Cover Image
    if (courseInfo) {
      const coverImage = courseInfo.coverImage || null;
      if (coverImage) {
        const coverUrl = coverImage.startsWith('http') ? coverImage : (app.forum.attribute('baseUrl') || '') + '/' + coverImage.replace(/^\//, '');
        if (!isEmojiImageUrl(coverUrl)) return coverUrl;
      }
    }
    // 排除 emoji 图片
    if (customThumbnail && !isEmojiImageUrl(customThumbnail)) {
      return customThumbnail;
    }
    return null;
  };

  // 扩展 contentItems，在开头添加缩略图（比 view 扩展更可靠）
  extend(DiscussionListItem.prototype, 'contentItems', function (items) {
    const thumbnail = this.getThumbnailUrl();
    if (thumbnail) {
      const discussion = this.attrs.discussion;
      items.add('thumbnail', (
        <Link href={app.route.discussion(discussion)} className="DiscussionListItem-thumbnail-link">
          <div className="DiscussionListItem-thumbnail">
            <img src={thumbnail} onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        </Link>
      ), 110);
    }
  });

  // 扩展 view 方法，删除 DiscussionListItem-content 中的重复头像
  extend(DiscussionListItem.prototype, 'view', function (vnode) {
    this.removeDuplicateAvatarFromContent(vnode);
  });

  // 扩展item-terminalPost，在内部添加作者头像、文章详情和增强信息
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    const discussion = this.attrs.discussion;
    const discussionId = discussion.id;
    
    // 移除标签项
    if (items.has('tags')) {
      items.remove('tags');
    }
    
    // 检查是否已经有作者头像存在（避免重复添加）
    const hasExistingAvatar = items.has('author');
    const hasCustomAvatar = items.has('customAuthorAvatar');
      
    
    if (!hasExistingAvatar && !hasCustomAvatar) {
      // 在item-terminalPost内部添加作者头像
      // 使用较低的优先级，确保不干扰FoF插件
      const avatarView = this.authorAvatarView();
      if (avatarView) {
        items.add('customAuthorAvatar', avatarView, 10);
      }
    }
    
    // 付费课程锁图标（个人中心/列表通用）
    if (this.isPaidCourse && this.isPaidCourse()) {
      items.add('paidCourseLock', m('span', {
        className: 'DiscussionListItem-paidCourseLock',
        title: app.translator.trans('tf-cns-courses.forum.paid_course_lock_tooltip') || 'Paid course'
      }, icon('fa-solid fa-lock')), 15);
    }
    
    // 添加文章详情（摘要）
    // 使用较低的优先级，确保它在作者信息之后但在增强信息之前
    const contentExcerpt = this.contentExcerptView();
    if (contentExcerpt) {
      items.add('contentExcerpt', contentExcerpt, -50);
    }
    
    // 在 infoItems 末尾添加增强信息容器
    // 使用最低优先级，确保它在所有其他 info items 之后
    const enhancedInfo = this.enhancedInfoView();
    if (enhancedInfo) {
      items.add('enhancedInfo', enhancedInfo, -100);
    }
  });
  // extend(DiscussionListItem.prototype, 'view', function (vnode) {
  //   const discussion = this.attrs.discussion;
  //   const thumbnail = discussion.data.attributes.customThumbnail;
  //   const enableThumbnail = app.forum.attribute('custom-discussions-item.enable_thumbnail') !== false;
    
  //   console.log('缩略图检测:', {
  //     thumbnail: thumbnail,
  //     enableThumbnail: enableThumbnail,
  //     discussionId: discussion.id
  //   });
    
  //   // if (thumbnail && enableThumbnail) {
  //     // 查找内容容器并添加缩略图
  //     this.addThumbnailToDiscussion(vnode, thumbnail);
  //   // }
  // });

  // 添加缩略图显示方法
  DiscussionListItem.prototype.addThumbnailToDiscussion = function (vnode, thumbnail) {
    
    if (!vnode || !vnode.children) {
      return;
    }
    
    const discussion = this.attrs.discussion;
    
    // 创建缩略图元素 - 使用固定80x80像素尺寸，外层包裹链接
    const thumbnailElement = m(Link, {
      href: app.route.discussion(discussion),
      className: 'DiscussionListItem-thumbnail-link'
    }, m('div', {
      className: 'DiscussionListItem-thumbnail'
    }, m('img', {
      src: thumbnail,
      onload: function() {
      },
      onerror: function() {
        this.style.display = 'none';
      }
    })));
    
    // 查找DiscussionListItem-content容器
    for (let child of vnode.children) {
      if (child && child.attrs && child.attrs.className && 
          child.attrs.className.includes('DiscussionListItem-content')) {
        
        // 查找作者信息容器
        for (let contentChild of child.children || []) {
          if (contentChild && contentChild.attrs && contentChild.attrs.className && 
              contentChild.attrs.className.includes('DiscussionListItem-author')) {
            
            // 在作者信息容器中添加缩略图
            if (!contentChild.children) contentChild.children = [];
            
            // 将缩略图添加到作者信息容器的开头
            contentChild.children.unshift(thumbnailElement);
            break;
          }
        }
        break;
      }
    }
  };

  // 添加缩略图到内容容器的方法
  DiscussionListItem.prototype.addThumbnailToContent = function (vnode, thumbnail) {
    
    if (!vnode || !vnode.children) {
      return;
    }
    
    const discussion = this.attrs.discussion;
    
    // 创建缩略图元素 - 使用固定80x80像素尺寸，外层包裹链接
    const thumbnailElement = m(Link, {
      href: app.route.discussion(discussion),
      className: 'DiscussionListItem-thumbnail-link'
    }, m('div', {
      className: 'DiscussionListItem-thumbnail'
    }, m('img', {
      src: thumbnail,
      onload: function() {
      },
      onerror: function() {
        this.style.display = 'none';
      }
    })));
    
    // 查找DiscussionListItem-content容器
    for (let child of vnode.children) {
      if (child && child.attrs && child.attrs.className &&
          child.attrs.className.includes('DiscussionListItem-content')) {
        
        // 将缩略图添加到内容容器的开头
        if (!child.children) child.children = [];
        child.children.unshift(thumbnailElement);
        
        // 隐藏紧跟在缩略图后面的重复头像
        this.hideDuplicateAvatar(child);
        break;
      }
    }
  };

  // 从DiscussionListItem-content中删除重复头像的方法
  DiscussionListItem.prototype.removeDuplicateAvatarFromContent = function (vnode) {
    if (!vnode || !vnode.children) {
      return;
    }
    
    // 查找DiscussionListItem-content容器
    for (let child of vnode.children) {
      if (child && child.attrs && child.attrs.className &&
          child.attrs.className.includes('DiscussionListItem-content')) {
        
        if (!child.children) {
          continue;
        }
        
        // 查找并删除DiscussionListItem-content中的CustomDiscussionsItem-author-avatar
        for (let i = child.children.length - 1; i >= 0; i--) {
          const contentChild = child.children[i];
          if (contentChild && contentChild.attrs && contentChild.attrs.className &&
              contentChild.attrs.className.includes('CustomDiscussionsItem-author-avatar')) {
            
            // 删除这个重复的头像
            child.children.splice(i, 1);
            break;
          }
        }
        break;
      }
    }
  };

  // 删除重复头像的方法（保留用于缩略图添加后的清理）
  DiscussionListItem.prototype.hideDuplicateAvatar = function (contentChild) {
    if (!contentChild || !contentChild.children) {
      return;
    }
    
    // 查找并删除DiscussionListItem-content中的CustomDiscussionsItem-author-avatar
    for (let i = contentChild.children.length - 1; i >= 0; i--) {
      const child = contentChild.children[i];
      if (child && child.attrs && child.attrs.className &&
          child.attrs.className.includes('CustomDiscussionsItem-author-avatar')) {
        
        // 删除这个重复的头像
        contentChild.children.splice(i, 1);
        break;
      }
    }
  };

  // 添加缩略图显示方法
  DiscussionListItem.prototype.thumbnailView = function () {
    const discussion = this.attrs.discussion;
    
    // 读取后台配置
    const enableThumbnail = app.forum.attribute('custom-discussions-item.enable_thumbnail') !== false;
    
    // 如果禁用了，不显示
    if (!enableThumbnail) {
      return null;
    }
    
    // 获取缩略图URL
    const thumbnail = discussion.data.attributes.customThumbnail;
    
    // 如果没有缩略图，不显示
    if (!thumbnail) {
      return null;
    }
    
    return m('div', {
      className: 'DiscussionListItem-thumbnail'
    }, m('img', {
      src: thumbnail,
      onload: function() {
      },
      onerror: function() {
        this.style.display = 'none';
      }
    }));
  };

  // 添加作者头像显示方法（放在DiscussionListItem-info内）
  DiscussionListItem.prototype.authorAvatarView = function () {
    const discussion = this.attrs.discussion;
    
    // 读取后台配置
    const enableAuthorAvatar = app.forum.attribute('custom-discussions-item.enable_author_avatar') !== false;
    const avatarSize = parseInt(app.forum.attribute('custom-discussions-item.avatar_size')) || 32;
    
    // 如果禁用了，不显示
    if (!enableAuthorAvatar) {
      return null;
    }
    
    const author = discussion.user ? discussion.user() : null;
    
    // 如果没有作者信息，不显示
    if (!author) {
      return null;
    }
    
    // 检查作者是否有头像URL
    let avatarUrl = null;
    try {
      if (author.avatarUrl && typeof author.avatarUrl === 'function') {
        avatarUrl = author.avatarUrl();
      } else if (author.avatarUrl) {
        avatarUrl = author.avatarUrl;
      } else if (author.data && author.data.attributes && author.data.attributes.avatarUrl) {
        avatarUrl = author.data.attributes.avatarUrl;
      }
    } catch (e) {
      // 忽略错误，继续检查
    }
    
    // 如果没有头像URL，显示名字的第一个字母
    if (!avatarUrl || avatarUrl === '' || avatarUrl === null) {
      const displayName = author.displayName ? (typeof author.displayName === 'function' ? author.displayName() : author.displayName) : 
                          (author.username ? (typeof author.username === 'function' ? author.username() : author.username) : '');
      const firstLetter = displayName ? displayName.charAt(0).toUpperCase() : '?';
      
      return (
        <div className="CustomDiscussionsItem-author-avatar">
          <span
            className="CustomDiscussionsItem-author-avatar-text"
            style={{
              width: avatarSize + 'px',
              height: avatarSize + 'px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#e74c3c87',
              color: 'white',
              borderRadius: '50%',
              fontSize: '16px',
            }}
            title={displayName}
          >
            {firstLetter}
          </span>
        </div>
      );
    }
    
    // 如果有头像URL，使用原来的avatar函数
    return (
      <div className="CustomDiscussionsItem-author-avatar">
        {avatar(author, { size: avatarSize })}
      </div>
    );
  };

  // 判断是否为付费课程（用于列表锁与预览）
  DiscussionListItem.prototype.isPaidCourse = function () {
    const discussion = this.attrs.discussion;
    if (!discussion) return false;
    const courseInfo = discussion.attribute ? discussion.attribute('groupCourse') : (discussion.data && discussion.data.attributes && discussion.data.attributes.groupCourse);
    if (!courseInfo) return false;
    return (courseInfo.price > 0) || (courseInfo.courseType === 'paid');
  };

  // 添加文章详情（摘要）显示方法
  DiscussionListItem.prototype.contentExcerptView = function () {
    try {
      const discussion = this.attrs.discussion;
      
      // 如果没有讨论对象，不显示
      if (!discussion) {
        return null;
      }
      
      // 课程类型优先使用后台配置的 Preview Content
      const courseInfo = discussion.attribute ? discussion.attribute('groupCourse') : (discussion.data && discussion.data.attributes && discussion.data.attributes.groupCourse);
      if (courseInfo && courseInfo.previewContent) {
        const textContent = String(courseInfo.previewContent).replace(/<[^>]*>/g, '').trim();
        const excerpt = (truncate && typeof truncate === 'function') ? truncate(textContent, 200) : textContent.substring(0, 200);
        if (excerpt && excerpt.trim().length > 0) {
          return m('div', {
            className: 'item-contentExcerpt DiscussionListItem-contentExcerpt DiscussionListItem-contentExcerpt--preview'
          }, excerpt);
        }
      }
      
      // 获取第一条帖子
      let firstPost = null;
      try {
        firstPost = discussion.firstPost ? discussion.firstPost() : null;
      } catch (e) {
        // 如果 firstPost 方法不存在或出错，返回 null
        return null;
      }
      
      // 如果没有第一条帖子，不显示
      if (!firstPost) {
        return null;
      }
      
      // 优先使用 fof/synopsis 扩展提供的 synopsis 字段（已过滤图片描述）
      let synopsisText = null;
      try {
        // 方法1: 使用 synopsis() 方法
        if (discussion.synopsis && typeof discussion.synopsis === 'function') {
          synopsisText = discussion.synopsis();
        }
        // 方法2: 使用 attribute 方法
        if (!synopsisText && discussion.attribute && typeof discussion.attribute === 'function') {
          synopsisText = discussion.attribute('synopsis');
        }
        // 方法3: 从 data.attributes 获取
        if (!synopsisText && discussion.data && discussion.data.attributes) {
          synopsisText = discussion.data.attributes.synopsis;
        }
      } catch (e) {
        // 忽略错误，继续尝试其他方法
      }
      
      // 如果没有 synopsis，从 firstPost 获取并过滤 meta 元素
      let filteredContent = null;
      if (!synopsisText || (typeof synopsisText === 'string' && synopsisText.trim().length === 0)) {
        // 方法：先获取 HTML，移除所有 meta 元素，然后转换为纯文本
        let contentHtml = null;
        try {
          // 尝试获取 HTML 内容
          if (firstPost.contentHtml && typeof firstPost.contentHtml === 'function') {
            contentHtml = firstPost.contentHtml();
          } else if (firstPost.data && firstPost.data.attributes && firstPost.data.attributes.contentHtml) {
            contentHtml = firstPost.data.attributes.contentHtml;
          }
        } catch (e) {
          // 如果获取 HTML 失败，回退到 contentPlain
        }
        
        if (contentHtml && typeof contentHtml === 'string') {
          // 使用 DOM 操作移除所有 meta 元素
          try {
            // 创建一个临时 DOM 元素来解析 HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = contentHtml;
            
            // 移除所有包含 meta 类的元素
            const metaElements = tempDiv.querySelectorAll('.meta, [class*="meta"], [class*="Meta"]');
            metaElements.forEach(el => el.remove());
            
            // 移除所有包含 filename 类的元素
            const filenameElements = tempDiv.querySelectorAll('.filename, [class*="filename"], [class*="Filename"]');
            filenameElements.forEach(el => el.remove());
            
            // 移除所有包含 informations 类的元素
            const infoElements = tempDiv.querySelectorAll('.informations, [class*="informations"], [class*="Informations"]');
            infoElements.forEach(el => el.remove());
            
            // 获取纯文本内容（移除所有 HTML 标签）
            filteredContent = tempDiv.textContent || tempDiv.innerText || '';
          } catch (e) {
            // 如果 DOM 操作失败，回退到 contentPlain
            filteredContent = firstPost.contentPlain ? firstPost.contentPlain() : null;
          }
        } else {
          // 如果没有 HTML，使用 contentPlain
          try {
            filteredContent = firstPost.contentPlain ? firstPost.contentPlain() : null;
          } catch (e) {
            return null;
          }
        }
        
        // 如果仍然没有内容，不显示
        if (!filteredContent || (typeof filteredContent === 'string' && filteredContent.trim().length === 0)) {
          return null;
        }
        
        // 清理多余的空白字符
        filteredContent = filteredContent.replace(/\s+/g, ' ').trim();
      } else {
        // 如果已经有 synopsis，直接使用
        filteredContent = synopsisText;
      }
      
      // 截断文本到合适长度（约200字符，确保在2行内显示）
      let excerpt = '';
      if (truncate && typeof truncate === 'function') {
        try {
          excerpt = truncate(filteredContent, 200);
        } catch (e) {
          excerpt = filteredContent.substring(0, 200);
        }
      } else {
        excerpt = filteredContent.substring(0, 200);
      }
      
      if (!excerpt || excerpt.trim().length === 0) {
        return null;
      }
      
      return m('div', {
        className: 'item-contentExcerpt DiscussionListItem-contentExcerpt'
      }, excerpt);
    } catch (error) {
      // 如果发生错误，返回 null 避免阻塞渲染
      console.error('Error in contentExcerptView:', error);
      return null;
    }
  };

  // 添加增强信息容器方法（包含likes、comments、share和reply avatars）
  DiscussionListItem.prototype.enhancedInfoView = function () {
    const discussion = this.attrs.discussion;
    
    // 始终显示容器（只要有内容）
    
    return (
      <div className="DiscussionListItem-enhanced">
        {/* 左侧：所有操作按钮（平级） */}
        <div className="DiscussionListItem-actions">
          {this.likesInfoView()}
          {this.commentsInfoView()}
          {this.shareInfoView()}
          {this.stickyInfoView()}
          {this.deleteButtonView()}
        </div>
        
        {/* 右侧：回帖用户头像 */}
        <div className="DiscussionListItem-reply-avatars">
          {this.replyAvatarsView()}
        </div>
      </div>
    );
  };

  // 添加删除按钮显示方法
  DiscussionListItem.prototype.deleteButtonView = function () {
    const discussion = this.attrs.discussion;
    
    // 检查是否有删除权限（使用 canDelete 而不是 canHide）
    let canDelete = false;
    try {
      // 方式1: 使用 canDelete() 方法（Flarum 标准方式）
      if (discussion.canDelete && typeof discussion.canDelete === 'function') {
        const result = discussion.canDelete();
        if (result !== undefined && result !== null) {
          canDelete = result === true || result === 1;
        }
      }
      
      // 方式2: 使用 attribute 方法
      if (!canDelete && discussion.attribute && typeof discussion.attribute === 'function') {
        const attrValue = discussion.attribute('canDelete');
        if (attrValue !== undefined && attrValue !== null) {
          canDelete = attrValue === true || attrValue === 1;
        }
      }
      
      // 方式3: 从 data.attributes 获取
      if (!canDelete && discussion.data && discussion.data.attributes) {
        const attrCanDelete = discussion.data.attributes.canDelete;
        if (attrCanDelete !== undefined && attrCanDelete !== null) {
          canDelete = attrCanDelete === true || attrCanDelete === 1;
        }
      }
    } catch (e) {
      // 如果检查权限出错，不显示按钮
      console.error('Error checking canDelete permission:', e);
      return null;
    }
    
    // 如果没有删除权限，不显示按钮
    if (!canDelete) {
      return null;
    }
    
    // 创建删除按钮（样式与其他按钮保持一致）；阻止触摸穿透到父级 a
    const LIST_ACTION_CLASS_DEL = 'touch-on-list-action';
    const stopAndDelete = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (e.type === 'touchend') document.body.classList.remove(LIST_ACTION_CLASS_DEL);
    };
    return m('span', {
      className: 'delete-button',
      role: 'button',
      tabindex: 0,
      ontouchstart: (e) => { e.stopPropagation(); document.body.classList.add(LIST_ACTION_CLASS_DEL); },
      onclick: (e) => {
        stopAndDelete(e);
        // 确认删除
        if (confirm(app.translator.trans('core.forum.discussion_controls.delete_confirmation'))) {
          // 调用原生的删除方法
          discussion.delete().then(() => {
            // 从 store 中移除已删除的讨论
            app.store.remove(discussion);
            
            // 获取当前路由
            const currentRoute = app.current.get('routeName');
            
            // 检查是否是个人中心页面（user.discussions 或 user）
            const isUserDiscussionsPage = currentRoute === 'user.discussions' || currentRoute === 'user';
            
            // 方法1: 尝试通过 app.discussions 的 removeDiscussion 触发全局事件
            // 这会通知所有监听 discussion.deleted 事件的 DiscussionListState 实例
            if (app.discussions && typeof app.discussions.removeDiscussion === 'function') {
              app.discussions.removeDiscussion(discussion);
            }
            
            // 方法2: 如果是个人中心页面，尝试通过路由刷新
            if (isUserDiscussionsPage) {
              // 尝试通过 app.current 获取组件
              const currentPage = app.current.get('component');
              if (currentPage && currentPage.state && typeof currentPage.state.refresh === 'function') {
                currentPage.state.refresh();
              } else {
                // 如果无法获取组件，延迟刷新路由（给事件处理时间）
                setTimeout(() => {
                  const username = m.route.param('username');
                  if (username) {
                    // 重新加载用户数据
                    app.store.find('users', username, { bySlug: true }).then(() => {
                      m.redraw();
                    });
                  }
                }, 100);
              }
            }
            
            // 刷新首页的讨论列表（如果存在）
            if (app.discussions && typeof app.discussions.refresh === 'function') {
              app.discussions.refresh();
            }
            
            // 强制重新渲染
            m.redraw();
          }).catch((error) => {
            console.error('Error deleting discussion:', error);
            alert(app.translator.trans('core.forum.discussion_controls.delete_error'));
          });
        }
      },
      ontouchend: stopAndDelete,
      style: 'cursor: pointer; touch-action: manipulation; -webkit-tap-highlight-color: transparent;'
    }, [
      icon('fa-light fa-trash-alt'),
      m('span', {
        className: 'delete-text'
      }, app.translator.trans('core.forum.discussion_controls.delete_button'))
    ]);
  };

  // 添加Likes信息显示方法
  DiscussionListItem.prototype.likesInfoView = function () {
    const discussion = this.attrs.discussion;
    
    // 获取第一条帖子（用于点赞功能）
    // 尝试多种方式获取 firstPost
    let firstPost = null;
    
    // 方式1: 使用 firstPost() 方法
    if (discussion.firstPost && typeof discussion.firstPost === 'function') {
      const post = discussion.firstPost();
      if (post && post !== false && post !== null) {
        firstPost = post;
      }
    }
    
    // 方式2: 从 relationships 中获取（如果方式1失败）
    if (!firstPost && discussion.data && discussion.data.relationships && discussion.data.relationships.firstPost) {
      const firstPostRel = discussion.data.relationships.firstPost;
      let firstPostData = null;
      
      // 处理不同的数据结构
      if (firstPostRel.data) {
        firstPostData = firstPostRel.data;
      } else if (firstPostRel.type && firstPostRel.id) {
        firstPostData = { type: firstPostRel.type, id: firstPostRel.id };
      }
      
      if (firstPostData) {
        // 从 store 中获取 post 对象
        const postId = firstPostData.id || (typeof firstPostData === 'string' ? firstPostData : null);
        if (postId) {
          firstPost = app.store.getById('posts', postId);
        }
      }
    }
    
    // 方式3: 如果 firstPost 是对象，直接使用
    if (!firstPost && discussion.firstPost && typeof discussion.firstPost === 'object') {
      firstPost = discussion.firstPost;
    }
    
    if (!firstPost) {
      return null;
    }
    
    // 列表只从接口 discussion.attributes 取
    const attrs = discussion.data && discussion.data.attributes ? discussion.data.attributes : {};
    const isLiked = attrs.isLiked === true;
    const likesCount = Number(attrs.likesCount) || 0;
    
    // 点赞点击：直接用外层的 firstPost（上面已保证存在），展示用 discussion.attributes
    const handleLikeClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (!app.session.user) {
        app.modal.show(LogInModal);
        return false;
      }
      if (firstPost._likeRequestInFlight) return false;

      const newIsLiked = !isLiked;
      firstPost._likeRequestInFlight = true;
      // 点赞后数量以接口返回为准（如 GET/PATCH /api/posts/6307 的 likesCount），不本地计算
      firstPost.save({ isLiked: newIsLiked }).then(() => {
        const attrs = firstPost.data && firstPost.data.attributes ? firstPost.data.attributes : {};
        const apiLikesCount = Number(attrs.likesCount);
        const apiIsLiked = attrs.isLiked === true;
        if (discussion.data && discussion.data.attributes) {
          if (attrs.likesCount !== undefined) discussion.data.attributes.likesCount = apiLikesCount;
          if (attrs.isLiked !== undefined) discussion.data.attributes.isLiked = apiIsLiked;
        }
        m.redraw();
      }).catch(function (err) { console.error('Error toggling like:', err); }).finally(function () {
        firstPost._likeRequestInFlight = false;
        m.redraw();
      });
      return false;
    };
    
    // 统一包装：阻止触摸/点击穿透到父级 a.DiscussionListItem-main，避免触发其 :active
    const LIST_ACTION_CLASS = 'touch-on-list-action';
    const likeLoading = !!(firstPost && firstPost._likeRequestInFlight);
    const stopAndLike = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (likeLoading) return;
      handleLikeClick(e);
      if (e.type === 'touchend') document.body.classList.remove(LIST_ACTION_CLASS);
    };
    return m('span', {
      className: 'likes-count' + (isLiked ? ' is-liked' : '') + (likeLoading ? ' is-loading' : ''),
      role: 'button',
      tabindex: likeLoading ? -1 : 0,
      ontouchstart: (e) => { e.stopPropagation(); document.body.classList.add(LIST_ACTION_CLASS); },
      onclick: stopAndLike,
      ontouchend: stopAndLike,
      style: 'cursor: ' + (likeLoading ? 'wait' : 'pointer') + '; touch-action: manipulation; -webkit-tap-highlight-color: transparent;' + (likeLoading ? ' pointer-events: none;' : '')
    }, [
      icon(isLiked ? 'fa-solid fa-heart' : 'fa-light fa-heart'),
      m('span', {
        className: 'count'
      }, String(likesCount))
    ]);
  };

  // 添加评论数显示方法
  DiscussionListItem.prototype.commentsInfoView = function () {
    const discussion = this.attrs.discussion;
    const commentCount = (discussion.commentCount() - 1) || 0;
    
    return m('span', {
      className: 'comments-count'
    }, [
      icon('fa-light fa-comment'),
      m('span', {
        className: 'count'
      }, String(commentCount))
    ]);
  };

  // 添加分享按钮显示方法（阻止触摸穿透到父级 a）
  DiscussionListItem.prototype.shareInfoView = function () {
    const discussion = this.attrs.discussion;
    const SHARE_ACTION_CLASS = 'touch-on-list-action';
    const stopShare = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (e.type === 'touchend') document.body.classList.remove(SHARE_ACTION_CLASS);
    };
    return m('span', {
      className: 'share-button',
      role: 'button',
      tabindex: 0,
      ontouchstart: (e) => { e.stopPropagation(); document.body.classList.add(SHARE_ACTION_CLASS); },
      onclick: (e) => {
        stopShare(e);
        if (discussion) {
          m.route.set(app.route.discussion(discussion));
        }
      },
      ontouchend: stopShare,
      style: 'touch-action: manipulation; -webkit-tap-highlight-color: transparent;'
    }, [
      icon('fa-light fa-share-alt'),
      m('span', {
        className: 'share-text'
      }, 'Share')
    ]);
  };

  // 添加置顶按钮显示方法
  DiscussionListItem.prototype.stickyInfoView = function () {
    const discussion = this.attrs.discussion;
    
    // 检查是否有置顶权限
    try {
      if (!discussion.canSticky || (typeof discussion.canSticky === 'function' && !discussion.canSticky())) {
        return null;
      }
    } catch (e) {
      // 如果 canSticky 方法不存在或出错，不显示置顶按钮
      return null;
    }
    
    const isSticky = discussion.isSticky && typeof discussion.isSticky === 'function' ? discussion.isSticky() : false;
    const STICKY_ACTION_CLASS = 'touch-on-list-action';
    const stopAndSticky = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (e.type === 'touchend') document.body.classList.remove(STICKY_ACTION_CLASS);
    };
    return m('span', {
      className: 'sticky-button' + (isSticky ? ' is-sticky' : ''),
      role: 'button',
      tabindex: 0,
      ontouchstart: (e) => { e.stopPropagation(); document.body.classList.add(STICKY_ACTION_CLASS); },
      onclick: (e) => {
        stopAndSticky(e);
        // 切换置顶状态
        discussion.save({ isSticky: !isSticky }).then(() => {
          // 刷新讨论列表
          if (app.discussions && typeof app.discussions.refresh === 'function') {
            app.discussions.refresh();
          }
          m.redraw();
        }).catch((error) => {
          console.error('Error toggling sticky:', error);
        });
      },
      ontouchend: stopAndSticky,
      style: 'touch-action: manipulation; -webkit-tap-highlight-color: transparent;'
    }, [
      icon('fa-light fa-thumbtack' + (isSticky ? '' : ' fa-rotate-90')),
      m('span', {
        className: 'sticky-text'
      }, isSticky ? app.translator.trans('flarum-sticky.forum.discussion_controls.unsticky_button') : app.translator.trans('flarum-sticky.forum.discussion_controls.sticky_button'))
    ]);
  };

  // 添加回帖用户头像列表显示方法
  DiscussionListItem.prototype.replyAvatarsView = function () {
    const discussion = this.attrs.discussion;
    
    // 读取后台配置
    const enableReplyAvatars = app.forum.attribute('custom-discussions-item.enable_reply_avatars') !== false;
    const maxAvatars = parseInt(app.forum.attribute('custom-discussions-item.max_reply_avatars')) || 5;
    const avatarSize = parseInt(app.forum.attribute('custom-discussions-item.avatar_size')) || 20;
    
    // 如果禁用了，不显示
    if (!enableReplyAvatars) {
      return null;
    }
    
    // 直接从API响应中获取回复用户信息
    const replyUsers = discussion.data.attributes.replyUsers || [];
    
    // 如果没有回帖用户，不显示
    if (replyUsers.length === 0) {
      return null;
    }

    // 限制显示的头像数量
    const displayUsers = replyUsers.slice(0, maxAvatars);

    return m('div', {
      className: 'DiscussionListItem-reply-avatars'
    }, [
      m('div', {
        className: 'avatars-list'
      }, [
        ...displayUsers.map((user, index) => {
          // 检查用户是否有有效的头像URL
          let avatarUrl = user.avatarUrl;
          if (avatarUrl && typeof avatarUrl === 'function') {
            avatarUrl = avatarUrl();
          }
          
          // 如果没有头像URL或为空，显示名字的第一个字母
          if (!avatarUrl || avatarUrl === '' || avatarUrl === null) {
            const displayName = user.displayName || user.username || '';
            const firstLetter = displayName ? displayName.charAt(0).toUpperCase() : '?';
            
            return m('span', {
              key: index,
              className: 'reply-avatar reply-avatar-text',
              title: displayName,
              style: `width: ${avatarSize}px; height: ${avatarSize}px; display: inline-flex; align-items: center; justify-content: center; background-color: #a0e5aa; color: white; border-radius: 50%; font-size: ${Math.floor(avatarSize * 0.4)}px; font-weight: bold;`
            }, firstLetter);
          }
          
          // 如果有头像URL，使用Flarum的avatar辅助函数
          const userModel = {
            id: () => user.id,
            displayName: () => user.displayName || user.username,
            username: () => user.username,
            avatarUrl: () => avatarUrl,
          };
          
          return m('span', {
            key: index,
            className: 'reply-avatar',
            title: user.displayName || user.username
          }, avatar(userModel, { size: avatarSize }));
        }),
        ...(replyUsers.length > maxAvatars ? [
          m('span', {
            key: 'more-avatars',
            className: 'more-avatars',
            title: app.translator.trans('custom-discussions-item.forum.more_replies', { count: replyUsers.length - maxAvatars })
          }, app.translator.trans('custom-discussions-item.forum.more_replies', { count: replyUsers.length - maxAvatars }))
        ] : [])
      ])
    ]);
  };
});